import type { Tool, Trace } from '@/features/traceability/type/Tool';

export type FilterCriteria = {
  machine?: string;
  material?: string;
  operation?: string;
};

/**
 * Filter traces based on machine, material, and operation criteria
 *
 * @param trace - The trace to check against filters
 * @param filters - Filter criteria (empty string means no filter for that field)
 * @returns true if trace matches all active filters
 */
export const matchesFilters = (trace: Trace, filters: FilterCriteria): boolean => {
  // Machine filter
  if (filters.machine && filters.machine !== '' && filters.machine !== '__all__') {
    if (trace.machine?.name !== filters.machine) {
      return false;
    }
  }

  // Material filter
  if (filters.material && filters.material !== '' && filters.material !== '__all__') {
    if ((trace.material?.name ?? '') !== filters.material) {
      return false;
    }
  }

  // Operation filter
  if (filters.operation && filters.operation !== '' && filters.operation !== '__all__') {
    if (trace.operation !== filters.operation) {
      return false;
    }
  }

  return true;
};

/**
 * Apply filters to a list of tools, filtering their traces
 *
 * @param tools - Array of tools with traces
 * @param filters - Filter criteria
 * @returns New array of tools with filtered traces
 */
export const filterTools = (tools: Tool[], filters: FilterCriteria): Tool[] => {
  // If no filters are active, return original tools
  const hasActiveFilters =
    (filters.machine && filters.machine !== '' && filters.machine !== '__all__') ||
    (filters.material && filters.material !== '' && filters.material !== '__all__') ||
    (filters.operation && filters.operation !== '' && filters.operation !== '__all__');

  if (!hasActiveFilters) {
    return tools;
  }

  // Filter traces for each tool
  return tools
    .map((tool) => ({
      ...tool,
      traces: Array.isArray(tool.traces)
        ? (tool.traces as Trace[]).filter((trace) => matchesFilters(trace, filters))
        : [],
    }))
    .filter((tool) => tool.traces.length > 0); // Remove tools with no matching traces
};
